import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CompanyContext = createContext(null);

export const CompanyProvider = ({ children }) => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch companies based on user's company_ids
  const fetchCompanies = useCallback(async () => {
    const token = localStorage.getItem("token");
    const userStr = localStorage.getItem("user");
    
    if (!token || !userStr) {
      setCompanies([]);
      setSelectedCompany(null);
      setLoading(false);
      return;
    }

    let user;
    try {
      user = JSON.parse(userStr);
    } catch (e) {
      console.error("Error parsing user");
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      let userCompanies = response.data;
      
      // Filter companies based on user's company_ids (unless admin)
      if (user.role !== 'admin' && user.company_ids && user.company_ids.length > 0) {
        userCompanies = response.data.filter(company => 
          user.company_ids.includes(company.id)
        );
      }
      
      console.log("User company_ids:", user.company_ids);
      console.log("Filtered companies:", userCompanies);
      
      setCompanies(userCompanies);
      
      // Auto-select if only one company
      if (userCompanies.length === 1) {
        selectCompany(userCompanies[0]);
      } else if (userCompanies.length > 0) {
        // Try to restore from localStorage or select first
        const savedCompanyId = localStorage.getItem("selectedCompanyId");
        const savedCompany = userCompanies.find(c => c.id === savedCompanyId);
        if (savedCompany) {
          selectCompany(savedCompany);
        } else {
          selectCompany(userCompanies[0]);
        }
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load and listen for user changes
  useEffect(() => {
    fetchCompanies();
    
    // Listen for user changes (login/logout)
    const handleUserChanged = () => {
      console.log("User changed, refetching companies...");
      fetchCompanies();
    };
    
    window.addEventListener("userChanged", handleUserChanged);
    
    return () => {
      window.removeEventListener("userChanged", handleUserChanged);
    };
  }, [fetchCompanies]);

  // Update localStorage when company changes
  const selectCompany = (company) => {
    console.log("Selecting company:", company);
    setSelectedCompany(company);
    if (company) {
      localStorage.setItem("selectedCompanyId", company.id);
    } else {
      localStorage.removeItem("selectedCompanyId");
    }
  };

  return (
    <CompanyContext.Provider
      value={{
        companies,
        setCompanies,
        selectedCompany,
        selectCompany,
        loading,
        companyId: selectedCompany?.id || null,
        refreshCompanies: fetchCompanies,
      }}
    >
      {children}
    </CompanyContext.Provider>
  );
};

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error("useCompany must be used within a CompanyProvider");
  }
  return context;
};
